import type { IPaymentInvoiceResponse } from 'models/payment';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { env } from '../../../config/env';
import type { ChargeSession, PaymentStatus } from '../../../models/charging';
import {
    createSettlementPaymentInvoice,
    getDownloadReceiptData,
    getPaymentDetails,
    stopCharge,
    tryAgainPayment,
} from '../../../services/charging/charging-service';
import type { IChargingProgressProps } from '../../../types/propTypes';
import { Common } from '../../../utils/constants';
import { getDNAPaymentScript, getPublicConfigurationValue } from '../../../utils/payment';

export const useChargingProgressHook = (props: IChargingProgressProps) => {
    const { t, chargeSession, isPaymentRetry, isSessionCompleted } = props;

    const { code, connector_id, invoice_id } = useParams();

    const [sessionDetails, setSessionDetails] = useState<ChargeSession | null>(null);
    const [graphData, setGraphData] = useState<number[][]>([[0, 0]]);
    const [showCompletedMessage, setShowCompletedMessage] = useState<boolean>(false);
    const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
    const [showWaitingMessage, setShowWaitingMessage] = useState<boolean>(false);
    const [paymentDetails, setPaymentDetails] = useState<PaymentStatus | null>(null);
    const [paymentFailMessage, setPaymentFailMessage] = useState<string>('');
    const [amountToPay, setAmountToPay] = useState<number | null>(null);
    const [thankYouMessage, setThankYouMessage] = useState<string>('');
    const [showTryAgain, setShowTryAgain] = useState<boolean>(false);
    const [showPayWithDifferentCard, setShowPayWithDifferentCard] = useState<boolean>(true);
    const [settlementAmount, setSettlementAmount] = useState<number>(0);
    const [payWithDifferentCardCount, setPayWithDifferentCardCount] = useState<number>(0);
    const [tryAgainCount, setTryAgainCount] = useState<number>(0);
    const [allPaymentsFailed, setAllPaymentsFailed] = useState<boolean>(false);
    const [allPaymentsFailedMessage, setAllPaymentsFailedMessage] = useState<string>('');
    const navigate = useNavigate();

    const getChargeDurationFromDate = (lastUpdateDateTime, startDateTime) => {
        lastUpdateDateTime = moment(lastUpdateDateTime);
        startDateTime = moment(startDateTime);
        return lastUpdateDateTime.diff(startDateTime, 'seconds');
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.get('success') === 'true') {
            localStorage.removeItem('tryAgainCount');
            localStorage.removeItem('payWithDifferentCardCount');
        }

        const storedTryAgainCount = localStorage.getItem('tryAgainCount');
        const storedPayWithDifferentCardCount = localStorage.getItem('payWithDifferentCardCount');

        if (storedPayWithDifferentCardCount) {
            setPayWithDifferentCardCount(parseInt(storedPayWithDifferentCardCount, 10));
            if (parseInt(storedPayWithDifferentCardCount, 10) >= 2) {
                setShowPayWithDifferentCard(false);
            }
        } else {
            localStorage.setItem('payWithDifferentCardCount', '0');
        }

        if (storedTryAgainCount) {
            setTryAgainCount(parseInt(storedTryAgainCount, 10));
            if (parseInt(storedTryAgainCount, 10) >= 1) {
                setShowTryAgain(false);
            }
        } else {
            localStorage.setItem('tryAgainCount', '0');
        }

        if (storedTryAgainCount && storedPayWithDifferentCardCount) {
            if (parseInt(storedTryAgainCount, 10) >= 1 && parseInt(storedPayWithDifferentCardCount, 10) >= 2) {
                setAllPaymentsFailed(true);
                setAllPaymentsFailedMessage(t<string>('chargingProgress.label.allPaymentsFailedMessage'));
            }
        }

        if (isSessionCompleted) {
            if (chargeSession && chargeSession.charge_percentage === 0 && Number(chargeSession.cost) === 0) {
                toast.error(t<string>('authoriseSession.unableToConnect'));
                navigate('/');
            }
            setShowCompletedMessage(true);
        }
        if (isPaymentRetry || isSessionCompleted) {
            setShowWaitingMessage(true);
            document.getElementById('loading').classList.remove('spinner-disable');
            setTimeout(async () => {
                document.getElementById('loading').classList.add('spinner-disable');
                setShowWaitingMessage(false);
                await handlePaymentDetails();
            }, 5000);
        }
    }, []);

    useEffect(() => {
        if (chargeSession && !isPaymentRetry && !isSessionCompleted) {
            const chargeDurationMinutes = getChargeDurationFromDate(
                chargeSession.last_update_time,
                chargeSession.start_time,
            );

            const graphUpdateArray = graphData;
            const graphUpdate = [chargeDurationMinutes, chargeSession.total_power_consumed];

            graphUpdateArray.push(graphUpdate);

            setSessionDetails(chargeSession);
            setGraphData(graphUpdateArray);

            if (chargeSession.charge_status === 'COMPLETED') {
                setShowCompletedMessage(true);
                setShowWaitingMessage(true);
                setTimeout(async () => {
                    await handlePaymentDetails();
                }, 5000);
            } else if (chargeSession.charge_status === 'INVALID') {
                setShowErrorMessage(true);
            }
        }
    }, [chargeSession]);

    const handleStopCharge = async () => {
        const token = localStorage.getItem(`SESSION_TOKEN_${code}`) || '';
        const session_id = sessionDetails?.session_id;
        const inputData = {
            invoice_id: invoice_id || '',
            token,
            session_id: session_id || '',
        };

        await stopCharge(inputData)
            .then((res) => {
                if (res.Data && res.Data.result === 'ACCEPTED') {
                    toast.success(t<string>('chargingProgress.label.stopSession'));
                    setShowCompletedMessage(true);
                    setShowWaitingMessage(true);
                    setTimeout(async () => {
                        await handlePaymentDetails();
                    }, 5000);
                }
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            });
    };

    const handleDownloadInvoice = async () => {
        try {
            const token = localStorage.getItem(`SESSION_TOKEN_${code}`) || '';
            const inputData = {
                token,
                invoice_id: invoice_id || '',
                is_download: true,
            };

            const res = await getDownloadReceiptData(inputData);
            const pdfDownloadLink = res.Data?.invoice_url;

            if (pdfDownloadLink) {
                const link = document.createElement('a');
                link.href = pdfDownloadLink;
                link.target = '_self';
                link.rel = 'noopener noreferrer';
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (err) {
            if (err.response?.data?.message) {
                toast.error(err.response.data.message);
            } else {
                toast.error(err.toString());
            }
        }
    };

    const handlePaymentDetails = async () => {
        const token = localStorage.getItem(`SESSION_TOKEN_${code}`) || '';

        const inputData = {
            invoice_id: invoice_id || '',
            token,
        };

        await getPaymentDetails(inputData)
            .then(async (res) => {
                const paymentData = res.Data;
                if (paymentData) {
                    const settledStatus = paymentData.find(
                        (status) =>
                            status.pre_auth_payment_status === 'SETTLED' &&
                            status.settlement_payment_status === 'SETTLED',
                    );
                    let amountToPay = 0;
                    let thankYouMessage = t('chargingProgress.label.thankYouForCharging');
                    let actualAmount = 0;
                    if (settledStatus) {
                        if (Number(settledStatus.settlement_amount) > 0) {
                            amountToPay = Number(
                                (
                                    Number(settledStatus.settlement_amount) + Number(settledStatus.pre_auth_amount)
                                ).toFixed(2),
                            );
                        } else {
                            amountToPay = Number(Number(settledStatus.pre_auth_amount).toFixed(2));
                        }
                        actualAmount = settledStatus.actual_amount ? Number(settledStatus.actual_amount) : 0;
                        if (settledStatus.charity_amount && Number(settledStatus.charity_amount) > 0) {
                            thankYouMessage = t('chargingProgress.label.thankYouWithCharity');
                            const charityAmount = Number(settledStatus.charity_amount);
                            thankYouMessage = thankYouMessage.replace('##CHARGE_AMOUNT##', actualAmount.toFixed(2));
                            thankYouMessage = thankYouMessage.replace('##CHARITY_AMOUNT##', charityAmount.toFixed(2));
                        } else {
                            thankYouMessage = t('chargingProgress.label.thankYouWithoutCharity');
                            thankYouMessage = thankYouMessage.replace('##CHARGE_AMOUNT##', actualAmount.toFixed(2));
                        }
                        setThankYouMessage(thankYouMessage);
                        setAmountToPay(amountToPay);
                        setPaymentDetails(settledStatus);
                    } else {
                        const preAuthFailedStatus = paymentData.find(
                            (status) =>
                                status.pre_auth_payment_status === 'FAILED' &&
                                (status.settlement_payment_status === 'FAILED' ||
                                    status.settlement_payment_status === 'SETTLEMENT_FAILED'),
                        );
                        if (preAuthFailedStatus) {
                            if (
                                Number(preAuthFailedStatus.settlement_amount) !== 0 &&
                                Number(preAuthFailedStatus.pre_auth_amount) !== 0
                            ) {
                                let paymentFailedMessage = t('chargingProgress.label.paymentFailed');

                                amountToPay = Number(
                                    (
                                        Number(preAuthFailedStatus.settlement_amount) +
                                        Number(preAuthFailedStatus.pre_auth_amount)
                                    ).toFixed(2),
                                );

                                paymentFailedMessage = paymentFailedMessage.replace(
                                    '##AMOUNT##',
                                    amountToPay.toString(),
                                );
                                const settelment_amount = Number(
                                    Number(preAuthFailedStatus.settlement_amount).toFixed(2),
                                );
                                setSettlementAmount(settelment_amount);
                                if (settelment_amount < 0) {
                                    amountToPay = Number(Number(preAuthFailedStatus.actual_amount).toFixed(2));
                                    paymentFailedMessage = t(
                                        'chargingProgress.label.settlementFailedWithNegativeValue',
                                    );
                                    if (parseInt(localStorage.getItem('tryAgainCount') || '', 10) >= 1) {
                                        setShowTryAgain(false);
                                        paymentFailedMessage = t('chargingProgress.label.tryAgainFailed');
                                    } else {
                                        setShowTryAgain(true);
                                    }
                                }
                                setPaymentFailMessage(paymentFailedMessage);
                                setAmountToPay(amountToPay);
                                setPaymentDetails(preAuthFailedStatus);
                            } else if (
                                Number(preAuthFailedStatus.settlement_amount) === 0 &&
                                Number(preAuthFailedStatus.pre_auth_amount) === 0
                            ) {
                                toast.info(t<string>('authoriseSession.unableToConnect'));
                                navigate('/');
                            }
                        } else {
                            const settlementFailedStatus = paymentData.find(
                                (status) =>
                                    status.pre_auth_payment_status === 'SETTLED' &&
                                    (status.settlement_payment_status === 'FAILED' ||
                                        status.settlement_payment_status === 'SETTLEMENT_FAILED'),
                            );

                            if (settlementFailedStatus) {
                                const settelment_amount = Number(
                                    Number(settlementFailedStatus.settlement_amount).toFixed(2),
                                );
                                setSettlementAmount(settelment_amount);

                                let paymentFailedMessage = '';

                                if (settelment_amount > 0) {
                                    amountToPay = Number(Number(settlementFailedStatus.settlement_amount).toFixed(2));
                                    paymentFailedMessage = t('chargingProgress.label.settlementFailed');
                                    paymentFailedMessage = paymentFailedMessage
                                        .replace(
                                            '##PRE_AUTH_AMOUNT##',
                                            settlementFailedStatus.pre_auth_amount.toString(),
                                        )
                                        .replace(
                                            '##SETTLEMENT_AMOUNT##',
                                            settlementFailedStatus.settlement_amount.toString(),
                                        );
                                } else if (settelment_amount < 0) {
                                    amountToPay = Number(Number(settlementFailedStatus.actual_amount).toFixed(2));
                                    paymentFailedMessage = t(
                                        'chargingProgress.label.settlementFailedWithNegativeValue',
                                    );
                                    if (parseInt(localStorage.getItem('tryAgainCount') || '', 10) >= 1) {
                                        setShowTryAgain(false);
                                        paymentFailedMessage = t('chargingProgress.label.tryAgainFailed');
                                    } else {
                                        setShowTryAgain(true);
                                    }
                                }
                                setPaymentFailMessage(paymentFailedMessage);
                                setAmountToPay(amountToPay);
                                setPaymentDetails(settlementFailedStatus);
                            }
                        }
                    }
                }

                setShowWaitingMessage(false);
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            });
    };

    const handlePayWithDifferentCard = async () => {
        const enable_google_apple_pay = await getPublicConfigurationValue(
            Common.ConfigurationKey.ENABLE_GOOGLE_PAY_APPLE_PAY_DNA,
        );
        const newPayWithDifferentCardCount = payWithDifferentCardCount + 1;
        localStorage.setItem('payWithDifferentCardCount', newPayWithDifferentCardCount.toString());
        const paymentInvoice = await createPaymentInvoice();
        if (paymentInvoice) {
            const amount = amountToPay || 0;
            const isEnableDonation = false;
            const donationAmount = 0;
            document.getElementById('loading').classList.remove('spinner-disable');
            const isTestMode = !!(
                paymentInvoice.isTestMode === undefined ||
                paymentInvoice.isTestMode === null ||
                paymentInvoice.isTestMode === true
            );
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = getDNAPaymentScript(isTestMode);
            script.nonce = 'ils+vsV+OAN1z2QUGBzoyQ==';
            script.async = true;
            script.onload = () => {
                window.DNAPayments.configure({
                    autoRedirectDelayInMs: 3000,
                    scopes: {
                        allowHosted: true,
                    },
                    paymentMethods: [
                        {
                            name: window.DNAPayments.paymentMethods.BankCard,
                        },
                        ...(enable_google_apple_pay === 'true'
                            ? [
                                  {
                                      name: window.DNAPayments.paymentMethods.GooglePay,
                                  },
                                  {
                                      name: window.DNAPayments.paymentMethods.ApplePay,
                                  },
                              ]
                            : []),
                    ],
                    isTestMode: isTestMode,
                    paymentTimeoutInSeconds: 900,
                    isEnableDonation,
                });

                const invoiceId = paymentInvoice.invoice_id;

                const paymentData = {
                    paymentSettings: {
                        terminalId: paymentInvoice.terminal,
                        returnUrl: `${window.location.origin}/portal/charge-station/${code}/${connector_id}/${invoice_id}/charge?paymentRetry=true&&success=true`,
                        failureReturnUrl: `${window.location.origin}/portal/charge-station/${code}/${connector_id}/${invoice_id}/charge?paymentRetry=true&&success=false`,
                        callbackUrl: `${env.VITE_API_SERVER}/${env.VITE_API_VERSION}/web-pay/payment/dna-webhook/${invoiceId}`,
                        failureCallbackUrl: `${env.VITE_API_SERVER}/${env.VITE_API_VERSION}/web-pay/payment/dna-webhook/${invoiceId}`,
                        cscMatrix: 22,
                        paMatrix: 3,
                    },
                    customerDetails: {
                        accountDetails: {
                            accountId: invoice_id,
                        },
                    },
                    periodic: {
                        periodicType: 'ucof',
                    },
                    invoiceId: invoiceId,
                    amount: +amount,
                    auth: { access_token: paymentInvoice.access_token },
                    language: 'en',
                    description: 'Payment to InstaVolt',
                    transactionType: paymentInvoice.transaction_type || 'SALE',
                    currency: paymentInvoice.currency,
                    donationAmount,
                };

                window.DNAPayments.openPaymentPage(paymentData);
            };
            script.onerror = () => {
                document.getElementById('loading').classList.add('spinner-disable');
                toast.error(t<string>('paymentFail.common.error'));
            };
            document.body.appendChild(script);
        }
    };

    const createPaymentInvoice = async () => {
        const token = localStorage.getItem(`SESSION_TOKEN_${code}`) || '';
        let paymentInvoice: IPaymentInvoiceResponse;
        const inputData = {
            parent_invoice_id: invoice_id || '',
            token,
        };
        await createSettlementPaymentInvoice(inputData)
            .then(async (res) => {
                if (res.Data) {
                    paymentInvoice = res.Data;
                }
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            });
        return paymentInvoice;
    };

    const handleTryAgain = async () => {
        const newTryAgainCount = tryAgainCount + 1;
        localStorage.setItem('tryAgainCount', newTryAgainCount.toString());
        setShowTryAgain(false);

        const token = localStorage.getItem(`SESSION_TOKEN_${code}`) || '';

        const inputData = {
            invoice_id: invoice_id || '',
            token,
        };

        await tryAgainPayment(inputData)
            .then((res) => {
                if (res.Data.settlement_payment_status === 'SETTLED') {
                    setShowCompletedMessage(true);
                    setShowWaitingMessage(true);
                    setTimeout(async () => {
                        await handlePaymentDetails();
                    }, 5000);
                    localStorage.removeItem('tryAgainCount');
                    localStorage.removeItem('payWithDifferentCardCount');
                } else {
                    setPaymentFailMessage(t<string>('chargingProgress.label.tryAgainFailed'));
                }
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            });
    };

    const backbtnclick = () => {
        navigate('/');
    };

    return {
        t,
        isPaymentRetry,
        isSessionCompleted,
        code,
        sessionDetails,
        graphData,
        showCompletedMessage,
        showErrorMessage,
        showWaitingMessage,
        paymentDetails,
        paymentFailMessage,
        amountToPay,
        thankYouMessage,
        showTryAgain,
        showPayWithDifferentCard,
        payWithDifferentCardCount,
        tryAgainCount,
        settlementAmount,
        allPaymentsFailed,
        allPaymentsFailedMessage,
        handleStopCharge,
        handleDownloadInvoice,
        handlePaymentDetails,
        handlePayWithDifferentCard,
        createPaymentInvoice,
        handleTryAgain,
        backbtnclick,
    };
};
